import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project-imports
import Loader from 'components/Loader';

// types
import { AuthProps, AppAuthContextType } from 'types/auth';
import { KeyedObject } from 'types/root';
import { fetchService } from 'utils/fetch';

import { API_URLS, COOKIE_DOMAIN } from 'common/constants';
import { attachErrorsToObj, getResponseMessages } from 'common/helpers/general';

const chance = new Chance();

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const getAuthUser = () => {
  let user = null;

  try {
    const auth = JSON.parse(window.localStorage.getItem('auth') || '');
    user = auth.session.user;

    // FOR NOW ADD these values so the vite design works
    // TODO - rename the vite design variables to actual user field names
    // id, email, name, role

    user.email = auth.session.settings.profile.email;
    user.name = user.display_name ? user.display_name : user.full_name;
    user.role = ""

  } catch (error) {
    user = null;
  }

  return user;
}

const setCookie = (name: string, value: string) => {
    if (document.cookie.indexOf(name + '=') !== -1) {
        let cookieString = `${name}=${value}; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax`;
        document.cookie = cookieString;
    }
}

const deleteCookie = (name: string) => {
    if (document.cookie.indexOf(name + '=') !== -1) {
        let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax`;
        document.cookie = cookieString;
    }
}

const setSession = (auth?: KeyedObject | null) => {
  if (auth) {
    if (auth.token) {
      // Reset the entire auth data
      localStorage.setItem('auth', JSON.stringify(auth));
      setCookie("token", "kofi")
      // document.cookie = `token=your_auth_token; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax`;
      return auth.session.user;
    } else {
      // This is a partial update so get old data to overwrite
      const auth2 = JSON.parse(localStorage.getItem("auth") || "{}")
      auth2.session = auth.session;
      localStorage.setItem('auth', JSON.stringify(auth2));
      setCookie("token", "kofi")
      // document.cookie = `token=your_auth_token; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax`;
      return auth2.session.user;
    }
  } else {
    // document.cookie = '';
    deleteCookie("token")
    localStorage.removeItem('auth');
  }
  return null;
};


// ==============================|| AppAuth CONTEXT & PROVIDER ||============================== //

const AppAuthContext = createContext<AppAuthContextType | null>(null);

export const AppAuthProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const user = getAuthUser()
        if (user) {

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        // console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (username_or_email: string, password: string, setData: KeyedObject) => {

    await fetchService.post(API_URLS.login, { username_or_email, password }).then(response => {
      if (!response.ok) {
        // callback(null, stateData, `HTTP error! status: ${response.status}`)
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        setData.setStatus({ success: true });
        const user = setSession({token: data.token, session: data.session});

        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user
          }
        });
      } else {
        setData.setErrors(
          attachErrorsToObj(
            {submit: data.message}, 
            getResponseMessages(data)
          )
        );
        setData.setStatus({ success: false });
      }
    })
    .catch(error => {
      console.error(error)
      setData.setErrors({ submit: error });
      setData.setStatus({ success: false });
    });

  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => {
    // TODO
  };

  const updateProfile = () => {};

  const refetchSession = async () => {

    await fetchService.get(API_URLS.getSession).then(response => {
      if (!response.ok) {
        // callback(null, stateData, `HTTP error! status: ${response.status}`)
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        const user = setSession({session: data.session});
      }
    })
    .catch(error => {
      console.error(error)
    });

  };


  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <AppAuthContext value={{ ...state, login, logout, resetPassword, updateProfile, refetchSession }}>{children}</AppAuthContext>;
};

export default AppAuthContext;
